import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { initWxJSSDK } from '../../services/WechatPayService';
import { logError, logInfo } from '../../utils/errorHandler';
import './InviteFriendsModal.css';

const placeholder = '/placeholder.svg';

export interface InviteFriendsModalProps {
  visible: boolean;
  onClose: () => void;
  shareUrl: string;
  teamName: string;
  teamAvatar?: string;
  headerImageUrl?: string;
  /** 海报底部机构名，缺省则不展示整行 */
  footerName?: string;
}

function isMobileUa(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    typeof navigator !== 'undefined' ? navigator.userAgent : ''
  );
}

function isWechatBrowser(): boolean {
  return /MicroMessenger/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');
}

async function saveImageWithWechatSDK(imageUrl: string): Promise<void> {
  try {
    try {
      await initWxJSSDK();
    } catch (error) {
      logError('[InviteFriendsModal] initWxJSSDK', error);
    }
    if (typeof (window as any).wx === 'undefined') {
      throw new Error('wx undefined');
    }
    const wx = (window as any).wx;
    await new Promise<void>((resolve, reject) => {
      wx.previewImage({
        current: imageUrl,
        urls: [imageUrl],
        success: () => {
          logInfo('[InviteFriendsModal] previewImage ok');
          alert('请长按图片保存到相册');
          resolve();
        },
        fail: (err: unknown) => reject(err),
      });
    });
  } catch (error) {
    logError('[InviteFriendsModal] WeChat previewImage', error);
    throw error;
  }
}

async function saveImageToMobile(imageUrl: string): Promise<void> {
  if (isWechatBrowser()) {
    try {
      await saveImageWithWechatSDK(imageUrl);
      return;
    } catch {
      /* fallback below */
    }
  }
  const img = document.createElement('img');
  img.src = imageUrl;
  img.style.cssText =
    'position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 10001; object-fit: contain; background: rgba(0,0,0,0.85);';
  img.onload = () => {
    document.body.appendChild(img);
    const tip = document.createElement('div');
    tip.textContent = '长按图片保存到相册';
    tip.style.cssText =
      'position: fixed; bottom: 50px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.75); color: white; padding: 12px 24px; border-radius: 20px; z-index: 10002; font-size: 14px;';
    document.body.appendChild(tip);
    const close = () => {
      if (img.parentNode) document.body.removeChild(img);
      if (tip.parentNode) document.body.removeChild(tip);
    };
    img.addEventListener('click', close);
    tip.addEventListener('click', close);
    setTimeout(() => {
      if (tip.parentNode) document.body.removeChild(tip);
    }, 3000);
  };
  img.onerror = () => {
    alert('图片加载失败，请稍后重试');
  };
}

function saveImageToDesktop(imageUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.download = filename;
  link.href = imageUrl;
  link.click();
}

export const InviteFriendsModal: React.FC<InviteFriendsModalProps> = ({
  visible,
  onClose,
  shareUrl,
  teamName,
  teamAvatar,
  headerImageUrl,
  footerName,
}) => {
  const posterRef = useRef<HTMLDivElement>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible || !shareUrl) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const url = await QRCode.toDataURL(shareUrl, {
          width: 296,
          margin: 1,
          color: { dark: '#000000', light: '#ffffff' },
        });
        if (!cancelled) setQrDataUrl(url);
      } catch (e) {
        logError('[InviteFriendsModal] QRCode', e);
        if (!cancelled) setQrDataUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, shareUrl]);

  const copyLink = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        return true;
      }
      window.prompt('复制链接：', shareUrl);
      return true;
    } catch {
      window.prompt('复制链接：', shareUrl);
      return true;
    }
  }, [shareUrl]);

  const handleWeChat = useCallback(async () => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: `${teamName} · 一起捐`,
          text: '邀请你加入帮帮团，一起献爱心',
          url: shareUrl,
        });
        return;
      } catch (e: unknown) {
        if ((e as Error)?.name === 'AbortError') return;
      }
    }
    await copyLink();
    alert('链接已复制，请打开微信粘贴发送给好友');
  }, [copyLink, shareUrl, teamName]);

  const handleCopyLink = useCallback(async () => {
    await copyLink();
    alert('已复制邀请链接');
  }, [copyLink]);

  const handleSavePoster = useCallback(async () => {
    if (!posterRef.current || saving) return;
    setSaving(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(posterRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imageUrl = canvas.toDataURL('image/png');
      const filename = `一起捐邀请_${teamName.slice(0, 20)}.png`;
      if (isMobileUa()) {
        await saveImageToMobile(imageUrl);
      } else {
        saveImageToDesktop(imageUrl, filename);
      }
    } catch (e) {
      logError('[InviteFriendsModal] save poster', e);
      alert('保存海报失败，可尝试截图保存');
    } finally {
      setSaving(false);
    }
  }, [saving, teamName]);

  if (!visible) return null;

  const headerStyle: React.CSSProperties = headerImageUrl
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 100%), url(${headerImageUrl})`,
      }
    : {};

  return (
    <div
      className="invite-friends-overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="invite-friends-inner">
        <div ref={posterRef} className="invite-friends-poster">
          <div className="invite-friends-poster-header" style={headerStyle} />
          <div className="invite-friends-poster-body">
            <div className="invite-friends-avatar-wrap">
              {teamAvatar ? (
                <img
                  src={teamAvatar}
                  alt=""
                  className="invite-friends-avatar"
                  onError={(e) => {
                    e.currentTarget.src = placeholder;
                  }}
                />
              ) : (
                <div className="invite-friends-avatar invite-friends-avatar-placeholder">👥</div>
              )}
            </div>
            <div className="invite-friends-title">{teamName}</div>
            <div className="invite-friends-sub">邀请你加入帮帮团，一起献爱心</div>
            <div className="invite-friends-qr-wrap">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="" className="invite-friends-qr" />
              ) : (
                <span className="invite-friends-qr-loading">二维码生成中…</span>
              )}
            </div>
            {footerName ? (
              <div className="invite-friends-footer">
                <span className="invite-friends-footer-dot" aria-hidden />
                <span>{footerName}</span>
              </div>
            ) : null}
          </div>
        </div>

        <div className="invite-friends-actions">
          <button type="button" className="invite-friends-action" onClick={handleWeChat}>
            <span className="invite-friends-action-icon invite-friends-action-icon-wechat invite-friends-wechat-mark" aria-hidden>
              微
            </span>
            <span className="invite-friends-action-label">微信好友</span>
          </button>
          <button type="button" className="invite-friends-action" onClick={handleCopyLink}>
            <span className="invite-friends-action-icon invite-friends-action-icon-link" aria-hidden>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
              </svg>
            </span>
            <span className="invite-friends-action-label">复制链接</span>
          </button>
          <button type="button" className="invite-friends-action" onClick={handleSavePoster} disabled={saving}>
            <span className="invite-friends-action-icon invite-friends-action-icon-save" aria-hidden>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M12 15V3M8 7l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4 15v4a2 2 0 002 2h12a2 2 0 002-2v-4" strokeLinecap="round" />
              </svg>
            </span>
            <span className="invite-friends-action-label">{saving ? '生成中…' : '保存海报'}</span>
          </button>
        </div>

        <button type="button" className="invite-friends-cancel" onClick={onClose}>
          <span className="invite-friends-cancel-x">×</span>
          取消
        </button>
      </div>
    </div>
  );
};
